"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Card from "components/Card"
import Button from "components/Button"
import { ArrowLeft, Save, Plus, X } from "lucide-react"
import InputField from "components/InputField"
import { getCompleteDepartment, addDepartmentTitles } from "api/apiHelper"
import { toast } from "react-toastify"

interface Level {
  id: string
  code: string
  order: number
  promotion_timeline: string
}

interface Title {
  id: string
  name: string
  levelId: string
}

interface Department {
  id: string
  name: string
  levels: Level[]
  createdAt: string
}

const DepartmentDetail: React.FC = () => {
  const { departmentId } = useParams<{ departmentId: string }>()
  const navigate = useNavigate()
  const [department, setDepartment] = useState<Department | null>(null)
  const [levels, setLevels] = useState<Level[]>([])
  const [titles, setTitles] = useState<{ [levelId: string]: string[] }>({})
  const [savedTitles, setSavedTitles] = useState<Title[]>([])
  const [loading, setLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (departmentId) {
      fetchDepartmentDetails()
    }
  }, [departmentId])

  const fetchDepartmentDetails = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await getCompleteDepartment(departmentId!)

      const departmentData: Department = {
        id: response.data.id,
        name: response.data.name,
        levels: response.data.levels || [],
        createdAt: response.data.created_at || response.data.createdAt || new Date().toISOString(),
      }

      setDepartment(departmentData)
      setLevels([...departmentData.levels])

      const initialTitles: { [levelId: string]: string[] } = {}
      departmentData.levels.forEach((level) => {
        initialTitles[level.id] = [""]
      })

      if (response.data.titles) {
        const existingTitles: { [levelId: string]: string[] } = {}
        const savedTitlesData: Title[] = []

        response.data.titles.forEach((title: any) => {
          const levelId = title.level_id || title.levelId
          if (!existingTitles[levelId]) {
            existingTitles[levelId] = []
          }
          existingTitles[levelId].push(title.name)
          savedTitlesData.push({
            id: title.id,
            name: title.name,
            levelId: levelId,
          })
        })

        Object.keys(initialTitles).forEach((levelId) => {
          if (existingTitles[levelId]) {
            initialTitles[levelId] = existingTitles[levelId]
          }
        })

        setTitles(initialTitles)
        setSavedTitles(savedTitlesData)
      } else {
        setTitles(initialTitles)
      }
    } catch (err: any) {
      console.error("Error fetching department details:", err)
      setError(err?.response?.data?.error || err?.response?.data?.message || "Failed to fetch department details")
    } finally {
      setLoading(false)
    }
  }

  const handleLevelOrderChange = (levelId: string, newOrder: number) => {
    setLevels((prevLevels) =>
      prevLevels.map((level) =>
        level.id === levelId ? { ...level, order: newOrder } : level
      )
    )
  }

  const handleTitleChange = (levelId: string, titleIndex: number, value: string) => {
    setTitles((prev) => ({
      ...prev,
      [levelId]: prev[levelId]?.map((title, index) => (index === titleIndex ? value : title)) || [value],
    }))
  }

  const addTitleInput = (levelId: string) => {
    setTitles((prev) => ({
      ...prev,
      [levelId]: [...(prev[levelId] || []), ""],
    }))
  }

  const removeTitleInput = (levelId: string, titleIndex: number) => {
    setTitles((prev) => ({
      ...prev,
      [levelId]: prev[levelId]?.filter((_, index) => index !== titleIndex) || [],
    }))
  }

  const handleSaveTitles = async () => {
    if (!departmentId || !department) return

    try {
      setSaveLoading(true)
      setError(null)

      // Check for duplicate level orders
      const orderCounts: { [order: number]: number } = {}
      levels.forEach(level => {
        orderCounts[level.order] = (orderCounts[level.order] || 0) + 1
      })

      const duplicateOrders = Object.entries(orderCounts).filter(([_, count]) => count > 1)
      if (duplicateOrders.length > 0) {
        toast.error("Each level must have a unique order number")
        return
      }

      const titlesData: any[] = []
      Object.entries(titles).forEach(([levelId, levelTitles]) => {
        levelTitles
          .filter((title) => title.trim() !== "")
          .forEach((title) => {
            titlesData.push({
              level_id: levelId,
              name: title.trim(),
            })
          })
      })

      if (titlesData.length === 0) {
        toast.error("Please enter at least one title")
        return
      }

      const response = await addDepartmentTitles(departmentId, { 
        titles: titlesData,
        levels: levels.map(level => ({
          id: level.id,
          order: level.order
        }))
      })

      const newTitles: Title[] = titlesData.map((title, index) => ({
        id: response.data?.titles?.[index]?.id || `${Date.now()}-${index}`,
        name: title.name,
        levelId: title.level_id,
      }))

      setSavedTitles(newTitles)
      
      // Update department with new level orders
      if (department) {
        setDepartment({
          ...department,
          levels: levels
        })
      }

      toast.success("Titles and level orders saved successfully!")
    } catch (err: any) {
      console.error("Error saving titles:", err)
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || "Failed to save titles"
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setSaveLoading(false)
    }
  }

  const handleBack = () => {
    navigate("/organization-structure/levels/departments")
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-8">
          <div className="text-lg text-gray-600">Loading department details...</div>
        </div>
      </div>
    )
  }

  if (error && !department) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card extra="border-red-200 bg-red-50">
          <div className="p-6">
            <div className="text-red-800 font-medium mb-2">Error Loading Department</div>
            <div className="text-red-700 mb-4">{error}</div>
            <Button onClick={handleBack} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Departments
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  if (!department) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <div className="p-6 text-center">
            <div className="text-gray-600 mb-4">Department not found</div>
            <Button onClick={handleBack} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Departments
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">{department.name}</h1>
            <Button varient="outline" onClick={handleBack} className="flex items-center gap-2 bg-brand-500 hover:bg-brand-600 dark:hover:bg-brand-700 dark:bg-brand-400 text-white">
              <ArrowLeft className="w-4 h-4 " />
              Back
            </Button>
          </div>
          <p className="text-gray-600">Configure level orders and titles for each level in this department</p>
        </div>
      </Card>

      {error && (
        <Card extra="border-red-200 bg-red-50">
          <div className="p-4">
            <div className="text-red-800 font-medium">Error</div>
            <div className="text-red-700">{error}</div>
          </div>
        </Card>
      )}

      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-navy-700 dark:text-white">Level Configuration</h2>
              <p className="text-gray-600">Set level order numbers and add multiple titles for each organizational level</p>
            </div>
            <Button
              onClick={handleSaveTitles}
              className="flex items-center gap-2 bg-brand-500 hover:bg-brand-600 dark:hover:bg-brand-700 dark:bg-brand-400 text-white"
              disabled={
                saveLoading || Object.values(titles).every((levelTitles) => levelTitles.every((title) => !title.trim()))
              }
            >
              <Save className="w-4 h-4" />
              {saveLoading ? "Saving..." : "Save All Changes"}
            </Button>
          </div>

          <div className="space-y-6">
            {levels
              .sort((a, b) => a.order - b.order)
              .map((level) => (
                <div key={level.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 text-blue-800 rounded-full font-semibold">
                        {level.code}
                      </span>
                    </div>

                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Level Order</label>
                        <InputField
                          id={`level-order-${level.id}`}
                          placeholder="Enter level order"
                          type="number"
                          min="1"
                          value={level.order.toString()}
                          onChange={(e) => handleLevelOrderChange(level.id, Number(e.target.value) || 1)}
                          variant=""
                          extra="w-20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Promotion Timeline</label>
                        <p className="text-sm text-gray-600 pt-2">{level.promotion_timeline}</p>
                      </div>
                    </div>

                    <Button
                      varient="outline"
                      onClick={() => addTitleInput(level.id)}
                      className="flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Title
                    </Button>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700">Titles for this level:</h4>
                    {(titles[level.id] || [""]).map((title, titleIndex) => (
                      <div key={titleIndex} className="flex items-center gap-2">
                        <div className="flex-1">
                          <InputField
                            id={`title-${level.id}-${titleIndex}`}
                            label={titleIndex === 0 ? "Titles" : ""}
                            placeholder={`Enter title ${titleIndex + 1} for ${level.code}`}
                            value={title}
                            onChange={(e) => handleTitleChange(level.id, titleIndex, e.target.value)}
                            variant=""
                            extra=""
                          />
                        </div>
                        {titleIndex > 0 && (
                          <Button
                            varient="outline"
                            onClick={() => removeTitleInput(level.id, titleIndex)}
                            className="mt-6 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </Card>

      {savedTitles.length > 0 && (
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-navy-700 dark:text-white mb-2">Saved Configuration</h2>
            <p className="text-gray-600 mb-4">Successfully configured levels and titles for this department</p>

            <div className="space-y-4">
              {levels
                .sort((a, b) => a.order - b.order)
                .map((level) => {
                  const levelTitles = savedTitles.filter((title) => title.levelId === level.id)
                  if (levelTitles.length === 0) return null

                  return (
                    <div key={level.id} className="p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-green-100 text-green-800 rounded-full font-semibold text-sm">
                          {level.code}
                        </span>
                        <span className="font-medium text-green-800">Level Order: {level.order}</span>
                        <span className="text-green-600 text-sm">• {level.promotion_timeline}</span>
                      </div>
                      <div className="ml-10 space-y-1">
                        {levelTitles.map((title) => (
                          <div key={title.id} className="text-sm text-green-700">
                            • {title.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}

export default DepartmentDetail